import { useEffect, useState } from "react";
import { TablePager, TableSearch, TableText } from "@/components/table-tools";
import { createConnectQueryKey, useMutation, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";

import { adminListAccounts, adminSetAccountCohorts, adminSetWorkspaceEntitlement } from "@/gen/nodebase/v1/admin-AdminService_connectquery";
import { AccountCohort, type AdminAccount } from "@/gen/nodebase/v1/admin_pb";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ErrorNote, Loading, PageHeader } from "@/components/page";
import { FleetScope } from "@/components/scope";
import { ago, fmtBytes, fmtDate } from "@/lib/format";
import { cohortLabel, requestMeta } from "@/lib/feature-rollout";

const cohortOptions = [AccountCohort.STANDARD, AccountCohort.EARLY_ACCESS, AccountCohort.INTERNAL];

export function AdminAccounts() {
  const queryClient = useQueryClient();
  const accounts = useQuery(adminListAccounts, {}, { retry: false });
  const rows = accounts.data?.accounts ?? [];
  const [search, setSearch] = useState("");
  const [cohortFilter, setCohortFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [targets, setTargets] = useState<AdminAccount[] | null>(null);
  const [notice, setNotice] = useState("");
  const needle = search.trim().toLowerCase();
  const visible = rows.filter((a) => (!needle || `${a.email} ${a.id}`.toLowerCase().includes(needle)) && (cohortFilter === "all" || a.cohort === Number(cohortFilter)));
  const currentPage = Math.min(page, Math.max(0, Math.ceil(visible.length / 25) - 1));
  const pageRows = visible.slice(currentPage * 25, (currentPage + 1) * 25);
  useEffect(() => setSelected([]), [search, cohortFilter, currentPage]);
  const active24h = rows.filter((a) => a.lastActiveUnix > 0n && Date.now() / 1000 - Number(a.lastActiveUnix) < 24 * 3600).length;
  const traffic30d = rows.reduce((sum, a) => sum + a.traffic30dUpBytes + a.traffic30dDownBytes, 0n);
  const selectedRows = pageRows.filter((a) => selected.includes(a.id));
  const allPageSelected = pageRows.length > 0 && selectedRows.length === pageRows.length;

  return <>
    <PageHeader title="Accounts" scope={<FleetScope noun="Accounts" />} />
    <ErrorNote error={accounts.error} />
    {notice && <div role="status" className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">{notice}</div>}
    {accounts.isPending ? <Loading /> : accounts.isError ? null : <>
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Summary label="Registered accounts" value={String(rows.length)} />
        <Summary label="Device-active accounts (24h)" value={String(active24h)} />
        <Summary label="30d traffic" value={fmtBytes(traffic30d)} />
      </div>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1"><TableSearch label="Search accounts by email or ID" value={search} onChange={(v) => { setSearch(v); setPage(0); }} count={visible.length} total={rows.length} /></div>
        <label className="text-sm">Cohort<span className="mt-1 block"><Select aria-label="Filter by cohort" value={cohortFilter} onChange={(e) => { setCohortFilter(e.target.value); setPage(0); }}><option value="all">All cohorts</option>{cohortOptions.map((c) => <option key={c} value={c}>{cohortLabel[c]}</option>)}</Select></span></label>
        <Button disabled={!selectedRows.length} onClick={() => setTargets(selectedRows)}>Change cohort ({selectedRows.length})</Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">Cohorts control staged feature availability. They do not grant administrator access or change a subscription or billing plan.</p>
      <Table aria-label="Accounts"><TableHeader><TableRow>
        <TableHead><input aria-label="Select visible accounts" type="checkbox" checked={allPageSelected} onChange={(e) => setSelected(e.target.checked ? pageRows.map((a) => a.id) : [])} /></TableHead>
        <TableHead>Account</TableHead><TableHead>Cohort</TableHead><TableHead>Registered</TableHead><TableHead>Last device activity</TableHead><TableHead>Resources</TableHead><TableHead>30d traffic</TableHead><TableHead>User Exit quota</TableHead><TableHead>Edge Exit</TableHead>
      </TableRow></TableHeader><TableBody>
        {!visible.length && <TableRow><TableCell colSpan={9}>No accounts match these filters.</TableCell></TableRow>}
        {pageRows.map((a) => <TableRow key={a.id} data-testid="account-row">
          <TableCell><input aria-label={`Select ${a.email}`} type="checkbox" checked={selected.includes(a.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, a.id] : selected.filter((id) => id !== a.id))} /></TableCell>
          <TableCell><div className="font-medium"><TableText value={a.email} /></div><code className="text-xs text-muted-foreground">{a.id}</code></TableCell>
          <TableCell><div className="flex flex-col items-start gap-1"><Badge variant={a.cohort === AccountCohort.INTERNAL ? "warning" : a.cohort === AccountCohort.EARLY_ACCESS ? "success" : "secondary"}>{cohortLabel[a.cohort]}</Badge><Button variant="link" size="sm" className="h-auto p-0" onClick={() => setTargets([a])}>Change</Button></div></TableCell>
          <TableCell>{fmtDate(a.createdUnix)}</TableCell><TableCell>{ago(a.lastActiveUnix)}</TableCell>
          <TableCell><div>{a.workerCount} Worker · {a.deviceCount} Device</div><div className="text-xs text-muted-foreground">{a.workspaceCount} workspace{a.workspaceCount === 1 ? "" : "s"}</div></TableCell>
          <TableCell className="tabular-nums"><div>{fmtBytes(a.traffic30dUpBytes + a.traffic30dDownBytes)}</div><div className="text-xs text-muted-foreground">{String(a.traffic30dConnections)} connections</div></TableCell>
          <TableCell><ExitQuota enabled={Boolean(a.exitRegions)} quotaBytes={a.exitQuotaBytes} credits={a.exitCredits} /></TableCell>
          <TableCell><ExitRollout workspaceId={a.defaultWorkspaceId} value={a.exitRegions} /></TableCell>
        </TableRow>)}
      </TableBody></Table>
      <TablePager page={currentPage} pageSize={25} total={visible.length} onChange={setPage} />
    </>}
    <CohortDialog targets={targets} onClose={() => setTargets(null)} onResult={(message) => { setNotice(message); setSelected([]); setTargets(null); void queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: adminListAccounts, cardinality: undefined }) }); }} />
  </>;
}

function CohortDialog({ targets, onClose, onResult }: { targets: AdminAccount[] | null; onClose: () => void; onResult: (message: string) => void }) {
  const [cohort, setCohort] = useState(AccountCohort.STANDARD);
  const [reason, setReason] = useState("");
  const [savedRequest, setSavedRequest] = useState<{ meta: ReturnType<typeof requestMeta>; reason: string; changes: { accountId: string; cohort: AccountCohort; expectedRevision: bigint }[] } | null>(null);
  const change = useMutation(adminSetAccountCohorts);
  useEffect(() => { setCohort(targets?.[0]?.cohort ?? AccountCohort.STANDARD); setReason(""); setSavedRequest(null); change.reset(); }, [targets]);
  const changes = (targets ?? []).filter((a) => a.cohort !== cohort);
  const submit = () => {
    const request = savedRequest ?? { meta: requestMeta(), reason: reason.trim(), changes: changes.map((a) => ({ accountId: a.id, cohort, expectedRevision: a.cohortRevision })) };
    setSavedRequest(request);
    change.mutate(request, { onSuccess: (result) => onResult(result.applied ? `Updated ${changes.length} account${changes.length === 1 ? "" : "s"} to ${cohortLabel[cohort]}.` : `Nothing changed. Cohort data changed for ${result.conflictAccountIds.length} selected account${result.conflictAccountIds.length === 1 ? "" : "s"}; the table has been refreshed.`) });
  };
  return <Dialog open={Boolean(targets)} onOpenChange={(open) => !open && (savedRequest && change.error ? onResult("The request outcome is unknown. Account data has been refreshed; check it before deciding again.") : onClose())}><DialogContent><DialogHeader><DialogTitle>Change account cohort</DialogTitle><DialogDescription>This transaction updates all {targets?.length ?? 0} selected accounts together. A concurrent change cancels the entire batch.</DialogDescription></DialogHeader>
    <label className="text-sm font-medium">Target cohort<Select className="mt-1 w-full" value={cohort} disabled={Boolean(savedRequest)} onChange={(e) => setCohort(Number(e.target.value) as AccountCohort)}>{cohortOptions.map((c) => <option key={c} value={c}>{cohortLabel[c]}</option>)}</Select></label>
    <label className="text-sm font-medium">Reason (required)<textarea aria-label="Cohort change reason" className="mt-1 min-h-20 w-full rounded-md border bg-background p-2 font-normal" value={reason} disabled={Boolean(savedRequest)} onChange={(e) => setReason(e.target.value)} /></label>
    {change.error && <p role="alert" className="text-sm text-destructive">{change.error.message}</p>}
    <p className="text-sm">Confirm changing <strong>{changes.length}</strong> account{changes.length === 1 ? "" : "s"} to <strong>{cohortLabel[cohort]}</strong>.</p>
    <DialogFooter><Button variant="outline" onClick={() => savedRequest && change.error ? onResult("The request outcome is unknown. Account data has been refreshed; check it before deciding again.") : onClose()}>{savedRequest && change.error ? "Close and check current state" : "Cancel"}</Button><Button disabled={!reason.trim() || !changes.length || change.isPending} onClick={submit}>{change.isPending ? "Saving…" : change.error && savedRequest ? "Retry saved request" : "Confirm change"}</Button></DialogFooter>
  </DialogContent></Dialog>;
}

function ExitQuota({
  enabled,
  quotaBytes,
  credits,
}: {
  enabled: boolean;
  quotaBytes: bigint;
  credits: { region: string; availableBytes: bigint }[];
}) {
  if (!enabled) return <span>—</span>;
  return (
    <div className="tabular-nums" data-testid="exit-quota">
      <div>{fmtGiB(quotaBytes)}</div>
      <div className="whitespace-nowrap text-xs text-muted-foreground">
        {credits.length > 0
          ? credits
              .map((credit) => `${credit.region.toUpperCase()} ${fmtGiB(credit.availableBytes)}`)
              .join(" · ")
          : "No issued credit"}
      </div>
    </div>
  );
}

function fmtGiB(bytes: bigint): string {
  if (bytes === 0n) return "0 GiB";
  const gib = Number(bytes) / 1024 ** 3;
  if (gib < 0.1) return "<0.1 GiB";
  return `${gib < 10 ? gib.toFixed(1) : Math.round(gib)} GiB`;
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <Card data-testid="account-summary">
      <CardContent className="p-4">
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function ExitRollout({
  workspaceId,
  value,
}: {
  workspaceId: string;
  value: string;
}) {
  const queryClient = useQueryClient();
  const change = useMutation(adminSetWorkspaceEntitlement, {
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: adminListAccounts,
          cardinality: undefined,
        }),
      }),
  });

  if (!workspaceId) return <span>—</span>;
  return (
    <div>
      <Select
        aria-label="Edge Exit rollout"
        className="h-8 min-w-28"
        value={value}
        disabled={change.isPending}
        onChange={(event) =>
          change.mutate({
            workspaceId,
            key: "exit.regions",
            value: event.target.value,
          })
        }
      >
        <option value="">Off</option>
        <option value="hk,us">US + HK</option>
      </Select>
      {change.error && (
        <span className="mt-1 block text-xs text-destructive">
          {change.error.message}
        </span>
      )}
    </div>
  );
}
